/*
 * # Feature Flags
 *
 * Each flag has a default (`FEATURE_FLAG_DEFAULTS`). A value stored in `localStorage` overrides it and is checked at
 * render time; it persists across browser tabs and sessions until removed.
 *
 * ## Turning a flag on or off
 *
 * Open the browser console and run:
 * ```js
 * localStorage.setItem('ff_partners', 'true')   // on
 * localStorage.setItem('ff_register', 'false')  // off
 * ```
 * Then reload the page.
 *
 * ## Back to the default
 *
 * ```js
 * localStorage.removeItem('ff_register')
 * ```
 *
 * ## Adding a new flag
 *
 * 1. Add a new entry to `FeatureFlags` and its default to `FEATURE_FLAG_DEFAULTS` below.
 * 2. Use `isFeatureEnabled(FeatureFlags.YOUR_FLAG)` in the component.
 */

export const FeatureFlags = {
  FF_REGISTER: 'ff_register',
  FF_MEMBERSHIPS: 'ff_memberships',
  FF_PARTNERS: 'ff_partners',
  FF_GATE: 'ff_gate',
  FF_LOCKER: 'ff_locker',
} as const

type FeatureFlag = (typeof FeatureFlags)[keyof typeof FeatureFlags]

/** Whether each flag is on when nothing is stored for it. */
const FEATURE_FLAG_DEFAULTS: Record<FeatureFlag, boolean> = {
  [FeatureFlags.FF_REGISTER]: true,
  [FeatureFlags.FF_MEMBERSHIPS]: true,
  [FeatureFlags.FF_PARTNERS]: false,
  [FeatureFlags.FF_GATE]: false,
  [FeatureFlags.FF_LOCKER]: false,
}

const storedValue = (flag: FeatureFlag): string | null => {
  try {
    return localStorage.getItem(flag)
  } catch {
    // Storage unavailable (e.g. blocked site data): fall back to the default.
    return null
  }
}

export const isFeatureEnabled = (flag: FeatureFlag): boolean => {
  const stored = storedValue(flag)
  return stored === null ? FEATURE_FLAG_DEFAULTS[flag] : stored === 'true'
}
